import { memo, useCallback, useEffect, useRef } from 'react'
import { Tabbar } from 'common-components'
import { getTabsData, getTabContent } from './config'
import * as Styled from './styled'
import Footer from './GenerateTab/Footer'
import GenerateTab from './GenerateTab'
import { TabKey } from './consts'
import { ScrollableElementProvider } from './context'
import { aiVideoThunks } from 'store/features/aiVideo/thunks'
import { useSelector } from 'react-redux'
import store, { dispatch } from 'store'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { editorSelectors } from 'store/features/editor/selectors'
import { editorThunks } from 'store/features/editor/thunks'
import { userSelectors } from 'store/features/user/selectors'
import { PROJECT_MEDIA_TYPES } from 'consts'
import { OverlayItemType } from 'types'

const AIVideoContent = () => {
  const activeTab = useSelector(aiVideoSelectors.activeTab)
  const showVersionHistoryTab = useSelector(
    aiVideoSelectors.showVersionHistoryTab
  )
  const activeTimelineItem = useSelector(editorSelectors.activeTimelineItem)
  const isGenAiProject = !!useSelector(projectSelectors.meta)?.genAiProjectId
  const isFromDifferentProject = useSelector(
    aiVideoSelectors.isFromDifferentProject
  )
  const hasSelectedItem =
    (!isGenAiProject || isFromDifferentProject) &&
    activeTimelineItem.type === PROJECT_MEDIA_TYPES.aiVideo
  const scrollableElementRef = useRef<HTMLDivElement | null>(null)
  const tabsData = getTabsData()
  const isLoadingTabData = useSelector(aiVideoSelectors.isLoadingTabData)
  const isModelsFetched = useSelector(aiVideoSelectors.isModelsFetched)
  const showTabs = hasSelectedItem || showVersionHistoryTab
  const isFooterVisible = !showTabs || activeTab === TabKey.GENERATE
  const isProjectLoaded = useSelector(projectSelectors.isLoaded)
  const isUserLoggedIn = useSelector(userSelectors.isUserLoggedIn)

  const handleTabClick = useCallback(
    (tab: string) => {
      if (tab !== activeTab) {
        dispatch(aiVideoThunks.setActiveTab(tab))
      }
    },
    [activeTab]
  )

  useEffect(() => {
    if (hasSelectedItem && activeTimelineItem.id && activeTimelineItem.type) {
      dispatch(aiVideoThunks.setActiveTab(TabKey.GENERATE))
      dispatch(
        editorThunks.openItemTypeSpecificTab(
          activeTimelineItem.id,
          activeTimelineItem.type
        )
      )
    }
    // activeTimelineItem is intentionally excluded: we only want to re-run when
    // hasSelectedItem changes (e.g. on remount after mobile panel close, or on item
    // selection), not on every property change of the active item.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelectedItem])

  useEffect(() => {
    // Wait for user data — isFreeUser falls back to true until hdMax is known,
    // which would otherwise clamp the default quality to the free tier.
    if (isProjectLoaded && isUserLoggedIn) {
      dispatch(aiVideoThunks.getAIVideoModels())
    }
  }, [isProjectLoaded, isUserLoggedIn])

  useEffect(
    () => () => {
      const state = store.getState()
      const isTransitioningToSmartEdit =
        editorSelectors.activeAsideOverlayKey(state) ===
        OverlayItemType.AI_VIDEO

      if (!isTransitioningToSmartEdit) {
        const currentTargetId = aiVideoSelectors.targetMediaItemId(state)
        if (currentTargetId) {
          dispatch(aiVideoThunks.clearGenerationParams())
        }
      }
      dispatch(aiVideoThunks.setActiveTab(TabKey.GENERATE))
      dispatch(aiVideoThunks.setShowVersionHistoryTab(false))
    },
    // no deps to ensure it runs only on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // Keep the skeleton up from mount until the model catalog is fetched and the
  // preselected model is resolved — getAIVideoModels waits for project + user
  // data, so isLoadingTabData alone leaves a flash of the empty form before it.
  if (isLoadingTabData || !isProjectLoaded || !isModelsFetched) {
    return <Styled.AiContentLoadingWrapper />
  }

  return (
    <ScrollableElementProvider scrollableElementRef={scrollableElementRef}>
      <Styled.ContentWrapper>
        <Styled.AIVideoContentContainer $hasSelectedItem={showTabs}>
          {showTabs && (
            <Styled.StickyTabbarWrapper>
              <Tabbar
                data={tabsData.map(tab => ({ ...tab, id: tab.name }))}
                activeTabName={activeTab}
                setActiveTabName={handleTabClick}
              />
            </Styled.StickyTabbarWrapper>
          )}
          <Styled.AIVideoContentWrapper ref={scrollableElementRef}>
            {showTabs ? getTabContent(activeTab) : <GenerateTab />}
          </Styled.AIVideoContentWrapper>
        </Styled.AIVideoContentContainer>
        {isFooterVisible && <Footer />}
      </Styled.ContentWrapper>
    </ScrollableElementProvider>
  )
}

export default memo(AIVideoContent)
